import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./EnsuringResults.module.css";
import { useAccreditation } from "../../../hooks/useAccreditation";

const EnsuringResults = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const { formData, setFormData, loading, hydrated, fetching, editable, setEditable, handleDone } =
    useAccreditation({
      labId,
      section: "Ensuring Results",
      name: "SADCAS F134-B",
      auditorId: user._id,
      isAssessor: user.role === "ASSESSOR",
      isEditing,
      token,
    });

  const handleAnswerChange = (questionId, score, comment, points, content) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: { score, comment, points, content },
    }));
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Does the laboratory have a procedure for monitoring the validity of results? Is the monitoring planned and reviewed?",
          number: "7.3.7.1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. Is data recorded in such a way that trends and shifts are detected and where practicable, statistical techniques applied to review the results?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      questions2: [
        {
          questionId: "Q3",
          content:
            "c. Does the laboratory have an IQC procedure for monitoring the ongoing validity of examination results?",
          number: "7.3.7.2 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q4",
          content:
            "d. Does the procedure allow for detection of lot-to-lot reagent or calibrator variation?",
          number: "7.3.7.2 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q5",
          content:
            "e. Is third-party IQC material considered as alternative or addition to manufacturer-supplied control material?",
          number: "7.2.3.2",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q6",
          content:
            "f. Does the laboratory select IQC material that is fit for its intended purpose? Consider stability, matrix, reactivity, clinical challenge?",
          number: "7.3.7.2(b)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q7",
          content:
            "g. Is IQC performed at a frequency based on method stability and patient risk?",
          number: "7.3.7.2(d)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q8",
          content:
            "h. Is IQC data reviewed with defined acceptability criteria at regular intervals?",
          number: "7.3.7.2(f)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q9",
          content:
            "i. Does the laboratory prevent release of patient results if IQC fails?",
          number: "7.3.7.2(g)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      questions3: [
        {
          questionId: "Comment1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            topQuestion={false}
            accreditation
            addWidth
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            title="Internal Quality Control (IQC)"
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            topQuestion={false}
            showQuestion={false}
            showEmptyNumber={false}
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions3}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  if (!hydrated || fetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">
            Clause 7.3.7: Ensuring the validity of examination results
          </h2>
        </div>

        {user.role === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => { setIsEditing(true); setEditable(true); }}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 7.3.7: Ensuring Results has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, formData)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => setEditable(false)}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={loading ? "Submitting..." : "Update"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default EnsuringResults;